import { APIError } from '../errors/APIError';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';
import { NotificationRuleDto, NotificationTemplateDto, SyncNotificationPreferencesRequest } from '../contracts/adminNotifications';

export const useGetNotificationEvents = () => {
  return useAPIQuery(['getNotificationEvents'], async client => {
    const response = await client.adminNotifications.getEvents();
    if (response.status === 404) return [];
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useGetNotificationRules = () => {
  return useAPIQuery(['getNotificationRules'], async client => {
    const response = await client.adminNotifications.getRules();
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useCreateNotificationRule = () => {
  return useAPIMutation((client, query) => {
    return async (rule: NotificationRuleDto) => {
      const response = await client.adminNotifications.createRule({
        body: rule
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getNotificationRules']);
      return response.body;
    };
  });
};

export const useGetNotificationTemplates = () => {
  return useAPIQuery(['getNotificationTemplates'], async client => {
    const response = await client.adminNotifications.getTemplates();
    
    // Fallback for missing backend implementation
    if (response.status === 404) return [];
    if (response.status !== 200) throw APIError.fromResponse(response);
    
    return response.body;
  });
};

export const useCreateNotificationTemplate = () => {
  return useAPIMutation((client, query) => {
    return async (template: NotificationTemplateDto) => {
      const response = await client.adminNotifications.createTemplate({
        body: template
      });

      if (response.status === 404) {
        throw new Error('Templates API endpoint is not yet implemented on the backend.');
      }

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getNotificationTemplates']);
      return response.body;
    };
  });
};

export const useUpdateNotificationRule = () => {
  return useAPIMutation((client, query) => {
    return async (args: { id: string; rule: NotificationRuleDto }) => {
      const response = await client.adminNotifications.updateRule({
        params: { id: args.id },
        body: args.rule
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getNotificationRules']);
      return response.body;
    };
  });
};

export const useDeleteNotificationRule = () => {
  return useAPIMutation((client, query) => {
    return async (id: string) => {
      const response = await client.adminNotifications.deleteRule({
        params: { id },
        body: undefined
      });

      if (response.status !== 204) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getNotificationRules']);
      return true;
    };
  });
};

export const useUpdateNotificationTemplate = () => {
  return useAPIMutation((client, query) => {
    return async (args: { id: string; template: NotificationTemplateDto }) => {
      const response = await client.adminNotifications.updateTemplate({
        params: { id: args.id },
        body: args.template
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getNotificationTemplates']);
      return response.body;
    };
  });
};

export const useDeleteNotificationTemplate = () => {
  return useAPIMutation((client, query) => {
    return async (id: string) => {
      const response = await client.adminNotifications.deleteTemplate({
        params: { id },
        body: undefined
      });

      if (response.status !== 204) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getNotificationTemplates']);
      return true;
    };
  });
};

export const useSyncNotificationPreferences = () => {
  return useAPIMutation((client, query) => {
    return async (payload: SyncNotificationPreferencesRequest) => {
      const response = await client.adminNotifications.syncPreferences({
        body: payload
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getNotificationRules']);
      return true;
    };
  });
};
